"use client"

import { useEffect, useState, useCallback } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { CheckCircle, Loader2, Shield, Activity, BarChart3, Settings } from "lucide-react"

interface WelcomeScreenProps {
  user?: {
    username?: string
    name?: string
    email?: string
  } | null
  onComplete: () => void
}

export function WelcomeScreen({ user = null, onComplete }: WelcomeScreenProps) {
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)
  const [isCompleted, setIsCompleted] = useState(false)

  const steps = [
    { icon: Shield, label: "Initializing Security Engine", duration: 1000 },
    { icon: Activity, label: "Loading Real-Time Monitors", duration: 800 },
    { icon: BarChart3, label: "Preparing Analytics Dashboard", duration: 1200 },
    { icon: Settings, label: "Configuring System Settings", duration: 600 },
  ]

  // Memoize onComplete to prevent dependency issues
  const handleComplete = useCallback(() => {
    if (!isCompleted) {
      setIsCompleted(true)
      if (typeof onComplete === 'function') {
        onComplete()
      }
    }
  }, [onComplete, isCompleted])

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    let completionTimeout: NodeJS.Timeout | null = null
    
    let totalDuration = 0
    let currentDuration = 0

    const totalTime = steps.reduce((acc, step) => acc + step.duration, 0)

    interval = setInterval(() => {
      if (currentStep < steps.length && !isCompleted) {
        currentDuration += 50
        const overallProgress = ((totalDuration + currentDuration) / totalTime) * 100

        setProgress(Math.min(overallProgress, 100))

        if (currentDuration >= steps[currentStep].duration) {
          totalDuration += steps[currentStep].duration
          currentDuration = 0
          
          if (currentStep === steps.length - 1) {
            // Last step completed
            setProgress(100)
            if (interval) clearInterval(interval)
            completionTimeout = setTimeout(handleComplete, 500)
          } else {
            setCurrentStep((prev) => prev + 1)
          }
        }
      }
    }, 50)

    // Cleanup function
    return () => {
      if (interval) clearInterval(interval)
      if (completionTimeout) clearTimeout(completionTimeout)
    }
  }, [currentStep, handleComplete, isCompleted, steps.length])

  // Safe user data handling
  const getUserDisplayName = () => {
    if (!user) return "Admin"
    return user.username || user.name || user.email || "Admin"
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse delay-1000"></div>
      </div>

      <Card className="w-full max-w-lg bg-slate-800/50 backdrop-blur-xl border-slate-700 shadow-2xl relative z-10">
        <CardContent className="p-8 text-center space-y-8">
          {/* Header */}
          <div className="space-y-4">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center animate-pulse">
              <span className="text-3xl" role="img" aria-label="elephant lightning">🐘⚡</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Welcome Back!</h1>
              <p className="text-slate-300">
                Hello, <span className="text-purple-400 font-medium">{getUserDisplayName()}</span>
              </p>
              <p className="text-sm text-slate-400 mt-1">Initializing your advanced dashboard...</p>
            </div>
          </div>

          {/* Progress */}
          <div className="space-y-4">
            <Progress value={progress} className="h-2 bg-slate-700" />
            <p className="text-sm text-slate-300">{Math.round(progress)}% Complete</p>
          </div>

          {/* Steps */}
          <div className="space-y-3">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isStepCompleted = index < currentStep
              const isCurrent = index === currentStep && !isCompleted
              
              return (
                <div
                  key={`step-${index}`}
                  className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 ${
                    isStepCompleted || (index === currentStep && isCompleted)
                      ? "bg-green-900/30 border border-green-700/50"
                      : isCurrent
                        ? "bg-purple-900/30 border border-purple-700/50"
                        : "bg-slate-800/30 border border-slate-700/50"
                  }`}
                >
                  <div
                    className={`flex-shrink-0 ${
                      isStepCompleted || (index === currentStep && isCompleted)
                        ? "text-green-400" 
                        : isCurrent 
                          ? "text-purple-400" 
                          : "text-slate-500"
                    }`}
                  >
                    {isStepCompleted || (index === currentStep && isCompleted) ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : isCurrent ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span
                    className={`text-sm ${
                      isStepCompleted || (index === currentStep && isCompleted)
                        ? "text-green-300" 
                        : isCurrent 
                          ? "text-purple-300" 
                          : "text-slate-400"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              )
            })}
          </div>

          {/* Footer */}
          <div className="text-xs text-slate-500 space-y-1">
            <p><span role="img" aria-label="shield">🛡️</span> Advanced Anti-Bot Protection Active</p>
            <p><span role="img" aria-label="chart">📊</span> Real-Time Intelligence Engine Ready</p>
            <p><span role="img" aria-label="lightning">⚡</span> High-Performance Monitoring Enabled</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default WelcomeScreen
